/**
 * useAICostPreview — tiny hook for Part K's inline cost preview.
 *
 * Pass 2 Step 2 upgrade — the backend now returns per-provider rates
 * plus the tenant's active provider. The hook picks the effective
 * rate (per-provider override → legacy fallback) so BYOK tenants
 * see their own provider-rate estimates, not blended figures.
 *
 * Never blocks the calling flow — the preview is informational only.
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useI18n } from "@/contexts/I18nContext";

export function useAICostPreview() {
  const { t } = useI18n();
  const [rate, setRate] = useState(null);       // effective rate (number | null)
  const [detail, setDetail] = useState(null);   // full response for advanced callers
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let alive = true;
    api.get("/ai/cost-rate", { _silent: true })
      .then((r) => {
        if (!alive) return;
        const d = r.data || {};
        setDetail(d);
        setRate(d.rate_configured && d.effective_usd_per_unit != null
                  ? Number(d.effective_usd_per_unit) : null);
        setLoaded(true);
      })
      .catch(() => { if (alive) setLoaded(true); });
    return () => { alive = false; };
  }, []);

  const estimate = (unitsEstimate, itemCountLabel) => {
    if (!loaded) return "";
    // Round 33 · B1 — tenant pages show AI units, never a currency or provider name.
    const units = Number(unitsEstimate || 0);
    const unitTxt = units < 0.01 ? `< 0.01 ${t("common.aiUnits")}` : `~${units.toFixed(2)} ${t("common.aiUnits")}`;
    const forItems = itemCountLabel
      ? ` ${t("common.forItemsSuffix").replace("{label}", itemCountLabel)}`
      : "";
    return `${unitTxt}${forItems}`;
  };

  return { rate, loaded, estimate,
           activeProvider: detail?.active_provider || null,
           detail };
}
